import { useCallback, useEffect, useMemo, useState } from "react";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import { isValidUuid, sortDatafieldsByOrder, useConstant, useDebouncer } from "@iventis/utilities";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { IDataFieldService } from "@iventis/datafield-editor";
import { convertStylePropertiesOnStyleTypeChange, hasCategoriesChanged } from "./edit-layer-helpers";
import { ILayerService } from "./edit-layer-services";

interface ImagePreviewState {
    fileName: string;
    imageUrl: string;
}

interface UseLayerEditorOptions {
    initialLayer: MapLayer;
    layerService: ILayerService;
    dataFieldsService: IDataFieldService;
    existingLayerTemplateCategories: string[];
    layerTemplateCategories: string[];
    thumbnailPreview: ImagePreviewState;
}

/**
 * Manages the core layer editing state: saved vs latest layer, partial updates, debounced saving.
 * Does NOT compute canSave — that belongs in the component where loading states are available.
 * Keeps side effects (save calls, debounce scheduling) out of React state updaters.
 */
export const useLayerEditor = ({
    initialLayer,
    layerService,
    dataFieldsService,
    existingLayerTemplateCategories,
    layerTemplateCategories,
    thumbnailPreview,
}: UseLayerEditorOptions) => {
    const queryClient = useQueryClient();

    /*
        State
    */
    const [savedLayer, setSavedLayer] = useState(initialLayer);
    const [latestLayer, setLayer] = useState(savedLayer);
    const layerId = useConstant(() => (initialLayer.id?.length ? initialLayer.id : uuid()));

    /*
        Derived state
    */
    const orderedDataFields = useMemo(() => sortDatafieldsByOrder(latestLayer.dataFields), [latestLayer.dataFields]);
    const existsOnServer = isValidUuid(savedLayer.id);

    /*
        Save mutation
    */
    const { isLoading: isSavingLayer, mutateAsync: saveLayer } = useMutation({
        mutationFn: async (_layer?: MapLayer) => {
            const layerToSave = _layer ?? latestLayer;
            // Prevent a queued autosave from racing this explicit save request
            saveDebounceRef.current.cancel();
            if (latestLayer.id == null) {
                setLayer({ ...layerToSave, id: layerId });
                const newLayer = { ...layerToSave, id: layerId };
                await layerService.postLayer(newLayer, thumbnailPreview?.imageUrl, layerTemplateCategories);

                const dataFields = newLayer.dataFields ?? [];
                const mapObjectNameField = dataFields.find((df) => df.systemDataFieldName === SystemDataFieldName.MapObjectName);
                if (mapObjectNameField?.order !== 1) {
                    const batchSize = 10;
                    for (let i = 0; i < dataFields.length; i += batchSize) {
                        const batch = dataFields.slice(i, i + batchSize);
                        await Promise.all(batch.map((dataField) => dataFieldsService.putDataField({ ...dataField }, newLayer.id)));
                    }
                }
                setSavedLayer(newLayer);
            } else {
                await layerService.putLayer(layerToSave, thumbnailPreview?.imageUrl, layerTemplateCategories);
                setSavedLayer(layerToSave);
            }
            if (hasCategoriesChanged(existingLayerTemplateCategories, layerTemplateCategories)) {
                queryClient.setQueryData(["template-categories", initialLayer.id], layerTemplateCategories);
            }
        },
    });

    /*
        Debouncer
    */
    const saveDebounceRef = useDebouncer(saveLayer, 10000);
    useEffect(
        () => () => {
            saveDebounceRef.current.cancel();
        },
        []
    );

    /**
     * Performs a patch-like update on the layer, only updating those properties that are provided.
     * Uses a 10-second debounce for saves. Pass forceSave=true for immediate saves.
     */
    const setPartialLayer = useCallback(
        (updator: Partial<MapLayer> | ((layer: MapLayer) => Partial<MapLayer>), forceSave = false) => {
            setLayer((layer) => {
                let partialLayer = typeof updator === "function" ? updator(layer) : updator;

                // Track model colour/image metrics
                if (layer.styleType === StyleType.Model || layer.styleType === StyleType.LineModel) {
                    if (partialLayer.modelStyle?.customColour != null || partialLayer.lineModelStyle?.customColour != null) {
                        incrementMonitoringCount({ name: MetricName.MODEL_COLOUR_CHANGES });
                    }
                    if (partialLayer.modelStyle?.customImage != null || partialLayer.lineModelStyle?.customImage != null) {
                        incrementMonitoringCount({ name: MetricName.MODEL_IMAGE_CHANGES });
                    }
                }

                partialLayer = convertStylePropertiesOnStyleTypeChange(layer, partialLayer);

                const newLayer = {
                    ...layer,
                    ...partialLayer,
                    iconStyle: { ...layer.iconStyle, ...partialLayer.iconStyle },
                    pointStyle: { ...layer.pointStyle, ...partialLayer.pointStyle },
                    areaStyle: { ...layer.areaStyle, ...partialLayer.areaStyle },
                    lineModelStyle: { ...layer.lineModelStyle, ...partialLayer.lineModelStyle },
                    areaModelStyle: { ...layer.areaModelStyle, ...partialLayer.areaModelStyle },
                    modelStyle: { ...layer.modelStyle, ...partialLayer.modelStyle },
                    lineStyle: { ...layer.lineStyle, ...partialLayer.lineStyle },
                };

                // Keep debounce/save coupled to the same layer snapshot we just produced
                saveDebounceRef.current.cancel();
                if (forceSave) {
                    saveLayer(newLayer);
                } else {
                    saveDebounceRef.current(newLayer);
                }

                return newLayer;
            });
        },
        [saveLayer]
    );

    return {
        savedLayer,
        latestLayer,
        setLayer,
        setPartialLayer,
        saveLayer,
        isSavingLayer,
        orderedDataFields,
        existsOnServer,
        layerId,
        saveDebounceRef,
    };
};
